import React, { useState, useEffect } from "react";
import SocialMediaIcons from "../components/SocialMediaIcons";
import useMediaQuery from "../hooks/useMediaQuery";
import { motion } from "framer-motion";
import AnchorLink from "react-anchor-link-smooth-scroll";

// Array of hero images
const HeroImages = [
  "/assets/business-image-1.png",
  "/assets/business-image-2.png",
  "/assets/business-image-3.png",
  "/assets/business-image-4.png",
  "/assets/business-image-5.png",
];

const RotatingImages = () => {
  const [currentImage, setCurrentImage] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentImage((prevImage) => (prevImage + 1) % HeroImages.length);
    }, 80000); // Change image every 3 seconds
    return () => clearInterval(interval);
  }, []);

  return (
    <motion.div
      key={currentImage}
      initial={{ opacity: 0, scale: 0.9 }}
      animate={{ opacity: 1, scale: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.8 }}
      className="relative z-0 ml-20 before:absolute before:-top-20 before:-left-20 before:rounded-t-[400px]
      before:w-full before:max-w-[400px] md:before:max-w-[600px] before:h-full before:border-2 before:border-blue before:z-[-1]"
    >
      <img
        src={HeroImages[currentImage]}
        alt={`hero-${currentImage}`}
        className="hover:filter hover:saturate-200 transition duration-900 z-10 w-full max-w-[400px] md:max-w-[600px]"
        onError={(e) => {
          e.target.onerror = null;
          e.target.src = "/assets/fallback-image.png"; // Fallback image
        }}
      />
    </motion.div>
  );
};

const Landing = ({ setSelectedPage }) => {
  // Use custom hook for responsive breakpoints
  const isAboveLarge = useMediaQuery("(min-width: 1060px)");

  return (
    <section
      id="home"
      className="md:flex md:justify-between md:items-center gap-16 md:h-full py-10"
    >
      {/* IMAGE SECTION */}
      <div className="basis-3/5 z-10 mt-16 md:mt-32 flex justify-center md:order-2">
        {isAboveLarge ? (
          <RotatingImages />
        ) : (
          <motion.img
            initial={{ scale: 0.8, rotate: -10, opacity: 0 }}
            animate={{ scale: 1, rotate: 0, opacity: 1 }}
            transition={{ duration: 0.8, ease: "easeOut" }}
            alt="business-hero"
            className="z-10 w-full max-w-[400px] md:max-w-[600px]"
            src="/assets/business-image-1.png"
            onError={(e) => {
              e.target.onerror = null;
              e.target.src = "/assets/fallback-image.png"; // Fallback image
            }}
          />
        )}
      </div>

      {/* MAIN TEXT */}
      <div className="z-30 basis-2/5 mt-12 md:mt-32">
        {/* HEADINGS */}
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.5 }}
          transition={{ duration: 0.5 }}
          variants={{
            hidden: { opacity: 0, x: -50 },
            visible: { opacity: 1, x: 0 },
          }}
        >
          <h1 className="text-6xl font-playfair z-10 text-center md:text-start">
            Empower Your Business with{" "}
            <span
              className="xs:relative inline-block text-blue xs:font-semibold z-[-20] xs:before:content-brush 
              before:absolute before:-left-[25px] before:-top-[70px] before:w-full before:h-[0.9em] 
               before:-z-10"
            >
              MBT Solutions
            </span>
          </h1>

          <p className="mt-10 mb-7 text-md text-center md:text-start">
            We provide modern templates and cutting-edge AI integrations tailored to streamline your business, optimize workflows, and elevate your online presence.
          </p>
        </motion.div>

        {/* CALL TO ACTIONS */}
        <motion.div
          className="flex mt-5 justify-center md:justify-start"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.5 }}
          transition={{ delay: 0.2, duration: 0.5 }}
          variants={{
            hidden: { opacity: 0, x: -50 },
            visible: { opacity: 1, x: 0 },
          }}
        >
          <AnchorLink
            className="bg-gradient-to-r from-blue to-teal text-white rounded-sm py-3 px-7 font-semibold
              hover:bg-teal transition duration-500"
            onClick={() => setSelectedPage("services")}
            href="#services"
          >
            Explore Services
          </AnchorLink>
          <AnchorLink
            className="rounded-r-sm bg-gradient-to-r from-blue to-teal py-0.5 pr-0.5 ml-3"
            onClick={() => setSelectedPage("products")}
            href="#products"
          >
            <div className="bg-white text-blue hover:text-teal transition duration-500 w-full h-full flex items-center justify-center px-10 font-playfair">
              See Products
            </div>
          </AnchorLink>
        </motion.div>

        <motion.div
          className="flex mt-5 justify-center md:justify-start"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.5 }}
          transition={{ delay: 0.4, duration: 0.5 }}
          variants={{
            hidden: { opacity: 0, x: -50 },
            visible: { opacity: 1, x: 0 },
          }}
        >
          <SocialMediaIcons />
        </motion.div>
      </div>
    </section>
  );
};

export default Landing;
